import React from 'react';
import { kebabCase } from 'lodash';
import { useField } from 'formik';
import { Input } from 'syngenta-digital-cropwise-react-ui-kit';
import { FieldMessage, useFieldMessage } from 'components/UiKit/FormikFields';
import 'components/UiKit/FormikFields/disabledFields.css';

export type CurrencyValue = string | number | null;

const PREFIX = '$';

const groupThousands = (digits: string) => {
  const leadingLength = digits.length % 3 || 3;
  const groups = [digits.slice(0, leadingLength)];
  for (let index = leadingLength; index < digits.length; index += 3) {
    groups.push(digits.slice(index, index + 3));
  }
  return groups.join(',');
};

// grow-ui masked these fields with imask's `\$num` (thousands ',', radix '.',
// two decimals, prefix always shown), so "$1,234.56" is what the form held
// and what mapFormToApi still strips.
export const formatCurrency = (value: CurrencyValue | undefined) => {
  const text = value === null || value === undefined ? '' : String(value);
  const [whole = '', ...rest] = text.replace(/[^\d.]/g, '').split('.');
  const hasRadix = rest.length > 0;
  const digits = whole.replace(/^0+(?=\d)/, '');
  if (!digits && !hasRadix) {
    return PREFIX;
  }
  const fraction = rest.join('').slice(0, 2);
  const formatted = `${PREFIX}${groupThousands(digits)}`;
  return hasRadix ? `${formatted}.${fraction}` : formatted;
};

type FormikCurrencyInputProps = {
  name: string;
  id?: string;
  // Shown instead of the field's own value (the sections blank out prices
  // for members without financial access).
  value?: CurrencyValue;
  disabled?: boolean;
  'data-testid'?: string;
};

const FormikCurrencyInput = ({
  name,
  id,
  value,
  disabled = false,
  'data-testid': dataTestId
}: FormikCurrencyInputProps) => {
  const [field, , { setValue, setTouched }] = useField<CurrencyValue>(name);
  const { hasError, message } = useFieldMessage(name);
  const inputId = id ?? kebabCase(name);
  const shown = formatCurrency(value !== undefined ? value : field.value);

  return (
    <div className="h-8">
      <Input
        id={inputId}
        name={name}
        data-testid={dataTestId}
        className="cwf-kit-field"
        inputMode="decimal"
        disabled={disabled}
        value={shown}
        status={hasError ? 'error' : undefined}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const formatted = formatCurrency(event.target.value);
          setValue(formatted === PREFIX ? '' : formatted);
        }}
        onBlur={() => setTouched(true)}
      />
      <FieldMessage message={message} />
    </div>
  );
};

export default FormikCurrencyInput;
